import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null while checking, false if anonymous, object if logged in
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            // _skip401Handler: this initial probe is allowed to 401 — it just
            // means the user isn't logged in. Don't let the global handler
            // swallow it as a graceful empty response.
            const { data } = await api.get("/auth/me", { _skip401Handler: true });
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Hook the global axios 401 handler so any expired-session response
    // gracefully signs the user out instead of bubbling up as an
    // unhandled AxiosError (which previously rendered React's
    // "Uncaught runtime errors" overlay on tabs like Customers / Reports
    // / Gallery, etc.). The state update is deferred to the next
    // microtask so it never runs synchronously during render.
    useEffect(() => {
        const handleUnauthorized = () => {
            queueMicrotask(() => setUser(false));
        };
        setUnauthorizedHandler(handleUnauthorized);
        return () => setUnauthorizedHandler(null);
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post(
                "/auth/login",
                { email, password },
                { _skip401Handler: true },
            );
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (name, email, password) => {
        try {
            const { data } = await api.post(
                "/auth/register",
                { name, email, password },
                { _skip401Handler: true },
            );
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout", null, { _skip401Handler: true });
        } catch {
            /* noop */
        }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
